import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchAircraft,
  fetchAircraftById,
  createAircraft,
  updateAircraft,
  deleteAircraft,
  restoreAircraft,
  checkTailNumberExists,
} from '@/lib/api/aircraft'
import { broadcastInvalidation } from '@/lib/sync'
import type { AircraftFormData, AircraftFilters } from '@/types/aircrafts'
import { toast } from 'sonner'

/**
 * Query key factory for aircraft
 * Provides consistent keys for cache management
 */
export const aircraftKeys = {
  all: ['aircraft'] as const,
  lists: () => [...aircraftKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: AircraftFilters) =>
    [...aircraftKeys.lists(), organizationId, filters] as const,
  details: () => [...aircraftKeys.all, 'detail'] as const,
  detail: (id: string) => [...aircraftKeys.details(), id] as const,
}

/**
 * Fetch all aircraft for current organization with optional filtering
 */
export function useAircraftList(filters?: AircraftFilters) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: aircraftKeys.list(organizationId || '', filters),
    queryFn: () => fetchAircraft(organizationId!, filters),
    enabled: !!organizationId,
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a single aircraft by ID
 */
export function useAircraftById(id: string | undefined) {
  return useQuery({
    queryKey: aircraftKeys.detail(id || ''),
    queryFn: () => fetchAircraftById(id!),
    enabled: !!id,
  })
}

/**
 * Create a new aircraft
 */
export function useCreateAircraft() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (data: AircraftFormData) => {
      // Check for duplicate tail number before creating
      const exists = await checkTailNumberExists(
        currentOrganization!.id,
        data.tail_number
      )
      if (exists) {
        throw new Error(`Aircraft with tail number ${data.tail_number} already exists`)
      }
      return createAircraft(currentOrganization!.id, data)
    },
    onSuccess: () => {
      const keys = [aircraftKeys.lists(), ['aircraft-registry']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Aircraft created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create aircraft', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing aircraft
 */
export function useUpdateAircraft() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AircraftFormData> }) => {
      // Check for duplicate tail number if changing it
      if (data.tail_number) {
        const exists = await checkTailNumberExists(
          currentOrganization!.id,
          data.tail_number,
          id
        )
        if (exists) {
          throw new Error(`Aircraft with tail number ${data.tail_number} already exists`)
        }
      }
      return updateAircraft(id, data)
    },
    onSuccess: (_, { id }) => {
      const keys = [aircraftKeys.lists(), aircraftKeys.detail(id), ['aircraft-registry']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Aircraft updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update aircraft', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete an aircraft (soft delete)
 */
export function useDeleteAircraft() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteAircraft(id),
    onSuccess: () => {
      const keys = [aircraftKeys.lists(), ['aircraft-registry']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Aircraft deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete aircraft', {
        description: error.message,
      })
    },
  })
}

/**
 * Restore a soft-deleted aircraft
 */
export function useRestoreAircraft() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => restoreAircraft(id),
    onSuccess: () => {
      const keys = [aircraftKeys.lists(), ['aircraft-registry']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Aircraft restored successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to restore aircraft', {
        description: error.message,
      })
    },
  })
}
